import React, { useState, useEffect, useReducer, useContext } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";

import SearchIcon from "@material-ui/icons/Search";
import {
  AddCircle,
  Build,
  ContentCopy,
  DevicesFold,
  MoreVert,
  Edit,
  Delete,
  PlayArrow,
  Pause,
} from "@mui/icons-material";

import {
  Button,
  CircularProgress,
  Grid,
  Box,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Fab,
  useScrollTrigger,
  Slide,
  Divider,
} from "@mui/material";

import api from "../../services/api";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import FlowBuilderModal from "../../components/FlowBuilderModal";
import FlowTriggersModal from "../../components/FlowTriggersModal";
import FlashOnIcon from "@mui/icons-material/FlashOn";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import NewTicketModal from "../../components/NewTicketModal";
import { SocketContext } from "../../context/Socket/SocketContext";

// ── Cores por tipo de nó ─────────────────────────────────────────────────────
const NODE_COLORS = {
  start:            "#10b981",
  message:          "#3b82f6",
  menu:             "#8b5cf6",
  openai:           "#6366f1",
  gemini:           "#6366f1",
  typebot:          "#8b5cf6",
  input:            "#f59e0b",
  question:         "#f59e0b",
  condition:        "#ef4444",
  conditionCompare: "#ef4444",
  interval:         "#94a3b8",
  tag:              "#14b8a6",
  removeTag:        "#14b8a6",
  attendant:        "#f97316",
  ticket:           "#0ea5e9",
  randomizer:       "#a855f7",
  switchFlow:       "#ec4899",
  variable:         "#64748b",
  img:              "#3b82f6",
  audio:            "#3b82f6",
  video:            "#3b82f6",
  httpRequest:      "#0891b2",
  singleBlock:      "#3b82f6",
};

const NW = 26, NH = 11; // tamanho do rect de cada nó no preview

function FlowMiniPreview({ flowData }) {
  if (!flowData || !Array.isArray(flowData.nodes) || flowData.nodes.length === 0) {
    return null;
  }

  const W = 200, H = 90, PAD = 12;
  const { nodes, connections } = flowData;

  const xs = nodes.map(n => n.position.x);
  const ys = nodes.map(n => n.position.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const rangeX = (maxX - minX) || 1;
  const rangeY = (maxY - minY) || 1;

  const sx = x => PAD + ((x - minX) / rangeX) * (W - PAD * 2 - NW);
  const sy = y => PAD + ((y - minY) / rangeY) * (H - PAD * 2 - NH);
  const cx = x => sx(x) + NW / 2;
  const cy = y => sy(y) + NH / 2;

  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });

  return (
    <svg
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {Array.isArray(connections) && connections.map((conn, i) => {
        const src = nodeMap[conn.source];
        const tgt = nodeMap[conn.target];
        if (!src || !tgt) return null;
        return (
          <line
            key={i}
            x1={cx(src.position.x)} y1={cy(src.position.y)}
            x2={cx(tgt.position.x)} y2={cy(tgt.position.y)}
            stroke="#cbd5e1"
            strokeWidth="1"
            strokeDasharray="3 2"
          />
        );
      })}
      {nodes.map(node => (
        <rect
          key={node.id}
          x={sx(node.position.x)}
          y={sy(node.position.y)}
          width={NW}
          height={NH}
          rx={2}
          fill={NODE_COLORS[node.type] || "#94a3b8"}
          opacity={0.88}
        />
      ))}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];
    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });
    return [...state, ...newContacts];
  }
  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);
    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }
  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor:
        theme.palette.type === "dark"
          ? theme.palette.grey[800]
          : theme.palette.grey[50],
      borderRadius: 4,
      "& fieldset": { border: "none" },
      "&:hover": {
        backgroundColor:
          theme.palette.type === "dark"
            ? theme.palette.grey[700]
            : theme.palette.grey[100],
      },
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      fontSize: "0.95rem",
    },
  },
  // ── Grid card ────────────────────────────────────────────────────────────
  flowCard: {
    borderRadius: "4px !important",
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    cursor: "pointer",
    height: 210,
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[6],
      borderColor: theme.palette.primary.light,
    },
  },
  cardBanner: {
    height: 90,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.type === "dark" ? "#1e293b" : "#f1f5f9",
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    position: "relative",
    overflow: "hidden",
  },
  cardBody: {
    flex: 1,
    padding: "10px 12px 10px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    backgroundColor: theme.palette.background.paper,
  },
  flowName: {
    fontSize: "0.82rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    overflow: "hidden",
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 6,
  },
  triggerBadge: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 10px",
    borderRadius: 20,
    cursor: "pointer",
    fontSize: "0.72rem",
    fontWeight: 600,
    transition: "all 0.15s ease",
    userSelect: "none",
    "&.has-triggers": {
      backgroundColor: theme.palette.type === "dark" ? "#78350f" : "#fef3c7",
      color: theme.palette.type === "dark" ? "#fbbf24" : "#b45309",
      border: `1px solid ${theme.palette.type === "dark" ? "#92400e" : "#fde68a"}`,
    },
    "&.no-triggers": {
      backgroundColor: theme.palette.type === "dark" ? "#1f2937" : "#f3f4f6",
      color: theme.palette.text.secondary,
      border: `1px solid ${theme.palette.divider}`,
    },
    "&:hover": {
      opacity: 0.8,
      transform: "translateY(-1px)",
    },
  },
  cardActions: {
    display: "flex",
    gap: 2,
  },
  actionButton: {
    width: 28,
    height: 28,
    padding: 4,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
  },
  // ── Empty / Loading ───────────────────────────────────────────────────────
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(6),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  menu: {
    "& .MuiPaper-root": {
      borderRadius: 4,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: theme.shadows[8],
      minWidth: 180,
      backgroundColor: theme.palette.background.paper,
    },
    "& .MuiMenuItem-root": {
      padding: theme.spacing(1.5, 2),
      fontSize: "0.9rem",
      color: theme.palette.text.primary,
      "&:hover": { backgroundColor: theme.palette.action.hover },
    },
  },
  fab: {
    position: "fixed",
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    width: 64,
    height: 64,
    boxShadow: theme.shadows[6],
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: theme.shadows[12],
    },
    [theme.breakpoints.down("sm")]: {
      bottom: theme.spacing(2),
      right: theme.spacing(2),
      width: 56,
      height: 56,
    },
  },
}));

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="up" in={!trigger}>
      {children}
    </Slide>
  );
}

function FlowCard({ flow, onEdit, onDuplicate, onDelete, onNavigate, onTrigger, triggerCount, classes }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();

  const handleMenuOpen = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
  const handleMenuClose = () => setAnchorEl(null);
  const handleAction = (action) => { handleMenuClose(); setTimeout(action, 100); };

  const handleCardClick = (e) => {
    if (e.target.closest("button") || e.target.closest('[role="button"]')) return;
    onNavigate(flow.id);
  };

  return (
    <Card className={classes.flowCard} onClick={handleCardClick} elevation={0}>
      {/* Banner com mini preview do fluxo */}
      <div className={classes.cardBanner}>
        {flow.flow && Array.isArray(flow.flow.nodes) && flow.flow.nodes.length > 0 ? (
          <FlowMiniPreview flowData={flow.flow} />
        ) : (
          <DevicesFold style={{ color: theme.palette.text.disabled, fontSize: 36, opacity: 0.5 }} />
        )}
      </div>

      {/* Corpo do card */}
      <div className={classes.cardBody}>
        <Typography className={classes.flowName} title={flow.name}>
          {flow.name}
        </Typography>

        <div className={classes.cardFooter}>
          {/* Badge de gatilhos clicável */}
          <div
            className={`${classes.triggerBadge} ${triggerCount > 0 ? "has-triggers" : "no-triggers"}`}
            onClick={(e) => { e.stopPropagation(); onTrigger && onTrigger(); }}
            title="Gerenciar gatilhos"
          >
            <FlashOnIcon style={{ fontSize: 13 }} />
            <span>
              {triggerCount > 0
                ? `⚡ ${triggerCount} gatilho${triggerCount !== 1 ? "s" : ""}`
                : "Sem gatilhos"}
            </span>
          </div>

          <div className={classes.cardActions}>
            <IconButton
              className={classes.actionButton}
              size="small"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              title="Editar nome"
            >
              <Edit style={{ fontSize: 15 }} />
            </IconButton>
            <IconButton
              className={classes.actionButton}
              size="small"
              onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
              title="Duplicar"
            >
              <ContentCopy style={{ fontSize: 15 }} />
            </IconButton>
            <IconButton
              className={classes.actionButton}
              size="small"
              onClick={handleMenuOpen}
              title="Mais opções"
            >
              <MoreVert style={{ fontSize: 15 }} />
            </IconButton>
          </div>
        </div>
      </div>

      <Menu
        className={classes.menu}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={(e) => { e.stopPropagation(); handleAction(() => onNavigate(flow.id)); }}>
          <Build fontSize="small" style={{ marginRight: 12, color: theme.palette.text.secondary }} />
          Abrir fluxo
        </MenuItem>
        <Divider />
        <MenuItem onClick={(e) => { e.stopPropagation(); handleAction(() => onEdit()); }}>
          <Edit fontSize="small" style={{ marginRight: 12, color: theme.palette.text.secondary }} />
          Editar fluxo
        </MenuItem>
        <Divider />
        <MenuItem onClick={(e) => { e.stopPropagation(); handleAction(() => onDelete()); }}>
          <Delete fontSize="small" style={{ marginRight: 12, color: theme.palette.error.main }} />
          Excluir fluxo
        </MenuItem>
      </Menu>
    </Card>
  );
}

const FlowBuilder = ({ embedded = false }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const [triggerCounts, setTriggerCounts] = useState({});
  const [triggersFlow, setTriggersFlow] = useState(null); // { id, name }
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/flowbuilder");
          const flows = Array.isArray(data) ? data : (data.flows || []);
          setWebhooks(flows);
          dispatch({ type: "LOAD_CONTACTS", payload: flows });
          setHasMore(data.hasMore || false);
          setLoading(false);
          // Buscar contagem de gatilhos
          try {
            const tRes = await api.get("/flowcampaign");
            const allCampaigns = tRes.data?.flow || [];
            const counts = {};
            allCampaigns.forEach(c => {
              if (c.flowId) counts[c.flowId] = (counts[c.flowId] || 0) + 1;
            });
            setTriggerCounts(counts);
          } catch { /* silent - não bloqueia a listagem de fluxos */ }
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, reloadData]);

  useEffect(() => {
    const companyId = user.companyId;
    const onContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    };
    socket.on(`company-${companyId}-contact`, onContact);
    return () => { socket.disconnect(); };
  }, []);

  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleEditContact = (contact) => {
    setSelectedContactId(contact.id);
    setSelectedWebhookName(contact.name);
    setContactModalOpen(true);
  };

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await api.delete(`/flowbuilder/${webhookId}`);
      setDeletingContact(null);
      setConfirmOpen(false);
      setReloadData((old) => !old);
      toast.success("Fluxo excluído com sucesso");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateFlow = async (flowId) => {
    try {
      await api.post(`/flowbuilder/duplicate`, { flowId });
      setDeletingContact(null);
      setConfirmDuplicateOpen(false);
      setReloadData((old) => !old);
      toast.success("Fluxo duplicado com sucesso");
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => setPageNumber((prev) => prev + 1);

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  const filteredWebhooks = webhooks.filter((w) =>
    w.name.toLowerCase().includes(searchParam.toLowerCase())
  );

  return (
    <MainContainer>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => handleCloseOrOpenTicket(ticket)}
      />

      <FlowBuilderModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData((old) => !old)}
      />

      <ConfirmationModal
        title={deletingContact ? `Excluir fluxo "${deletingContact.name}"?` : ""}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => { if (deletingContact) handleDeleteWebhook(deletingContact.id); }}
      >
        Esta ação não pode ser desfeita. Todas as integrações relacionadas serão perdidas.
      </ConfirmationModal>

      <ConfirmationModal
        title={deletingContact ? `Duplicar fluxo "${deletingContact.name}"?` : ""}
        open={confirmDuplicateOpen}
        onClose={setConfirmDuplicateOpen}
        onConfirm={() => { if (deletingContact) handleDuplicateFlow(deletingContact.id); }}
      >
        Uma cópia do fluxo será criada para você editar.
      </ConfirmationModal>

      <MainHeader>
        {!embedded && <Title>Fluxos de Conversa ({webhooks.length})</Title>}
        <MainHeaderButtonsWrapper>
          <TextField
            className={classes.searchField}
            placeholder="Buscar fluxos..."
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" color="primary" onClick={handleOpenContactModal}>
            Novo Fluxo
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        {loading && !webhooks.length ? (
          <div className={classes.loadingContainer}>
            <CircularProgress style={{ color: theme.palette.primary.main }} />
          </div>
        ) : filteredWebhooks.length === 0 ? (
          <div className={classes.emptyState}>
            <DevicesFold style={{ fontSize: 64, color: theme.palette.text.disabled, display: "block", margin: "0 auto 16px" }} />
            <Typography style={{ fontSize: "1.1rem", fontWeight: 600, color: theme.palette.text.primary, marginBottom: 8 }}>
              {searchParam ? "Nenhum fluxo encontrado" : "Nenhum fluxo criado ainda"}
            </Typography>
            <Typography style={{ color: theme.palette.text.secondary, fontSize: "0.9rem" }}>
              {searchParam
                ? "Tente usar outros termos de pesquisa"
                : "Crie seu primeiro fluxo de conversa para automatizar atendimentos"}
            </Typography>
            {!searchParam && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenContactModal}
                startIcon={<AddCircle />}
                style={{ marginTop: 24, borderRadius: 4, textTransform: "none", fontWeight: 600 }}
              >
                Criar Primeiro Fluxo
              </Button>
            )}
          </div>
        ) : (
          <>
            <Grid container spacing={2}>
              {filteredWebhooks.map((flow) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={flow.id}>
                  <FlowCard
                    flow={flow}
                    classes={classes}
                    onEdit={() => handleEditContact(flow)}
                    onDuplicate={() => { setDeletingContact(flow); setConfirmDuplicateOpen(true); }}
                    onDelete={() => { setDeletingContact(flow); setConfirmOpen(true); }}
                    onNavigate={(id) => history.push(`/flowbuilder/${id}`)}
                    triggerCount={triggerCounts[flow.id] || 0}
                    onTrigger={() => setTriggersFlow({ id: flow.id, name: flow.name })}
                  />
                </Grid>
              ))}
            </Grid>

            {loading && webhooks.length > 0 && (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={24} style={{ color: theme.palette.primary.main }} />
              </Box>
            )}
          </>
        )}
      </Paper>

      {isMobile && (
        <HideOnScroll>
          <Fab className={classes.fab} onClick={handleOpenContactModal} aria-label="Criar fluxo">
            <AddCircle />
          </Fab>
        </HideOnScroll>
      )}

      {/* Modal de Gatilhos */}
      <FlowTriggersModal
        open={Boolean(triggersFlow)}
        onClose={() => {
          setTriggersFlow(null);
          // Atualizar contagem após fechar
          api.get("/flowcampaign").then(res => {
            const all = res.data?.flow || [];
            const counts = {};
            all.forEach(c => { if (c.flowId) counts[c.flowId] = (counts[c.flowId] || 0) + 1; });
            setTriggerCounts(counts);
          }).catch(() => {});
        }}
        flowId={triggersFlow?.id}
        flowName={triggersFlow?.name}
      />
    </MainContainer>
  );
};

export default FlowBuilder;
